import ReactMarkdown from "react-markdown";
import type { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import { useCoachNotes } from "@/lib/coach-notes";

// Explicit element styling for the coach note. We can't rely on Tailwind's
// `prose` classes here — the @tailwindcss/typography plugin isn't installed, so
// every `prose-*` utility is a no-op and Preflight has zeroed all element
// margins. These per-element classes give the welcome note real, comfortable
// paragraph/section spacing. `first:mt-0` keeps the block flush with the top.
const coachMarkdownComponents: Components = {
  p: ({ children }) => (
    <p className="mt-4 first:mt-0 leading-relaxed text-foreground">{children}</p>
  ),
  h1: ({ children }) => (
    <h3 className="mt-7 mb-2 first:mt-0 text-base font-semibold text-foreground">{children}</h3>
  ),
  h2: ({ children }) => (
    <h3 className="mt-7 mb-2 first:mt-0 text-base font-semibold text-foreground">{children}</h3>
  ),
  h3: ({ children }) => (
    <h3 className="mt-7 mb-2 first:mt-0 text-base font-semibold text-foreground">{children}</h3>
  ),
  ul: ({ children }) => (
    <ul className="mt-4 first:mt-0 list-disc space-y-2 pl-5 text-foreground">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="mt-4 first:mt-0 list-decimal space-y-2 pl-5 text-foreground">{children}</ol>
  ),
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
  a: ({ children, href }) => (
    <a href={href} className="text-primary underline hover:text-primary-soft">{children}</a>
  ),
  code: ({ children }) => (
    <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">{children}</code>
  ),
};

export function CoachNotesCard(): JSX.Element | null {
  const state = useCoachNotes();
  if (state.status !== "ready") return null;
  return (
    <section className="mb-8 rounded-card border border-border bg-card shadow-sm overflow-hidden">
      <div className="flex items-center gap-3 border-b border-border-subtle bg-gradient-to-r from-primary/10 to-primary-soft/5 px-6 py-4 sm:px-8">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-5 w-5"
            aria-hidden="true"
          >
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
          </svg>
        </span>
        <div className="min-w-0">
          <p className="text-[11px] font-semibold tracking-wide text-primary-soft">
            A note from your coach
          </p>
          <h2 className="text-lg font-semibold leading-tight text-foreground">{state.title}</h2>
        </div>
      </div>
      <div className="max-w-none px-6 py-6 text-muted-foreground sm:px-8">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeSanitize]}
          components={coachMarkdownComponents}
        >
          {state.bodyMarkdown}
        </ReactMarkdown>
      </div>
    </section>
  );
}
